import {
  getDefaultsPvSystem,
  useSite,
} from "@inrange/building-manager-api-client";
import {
  AllowStringForNumbers,
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
  SiteElectricalSystem,
  SiteElectricalSystemPlaceholder,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { Button, Col, Form, Row } from "react-bootstrap";
import FormInputPercentage from "../../FormInputPercentage";
import CollapsibleCard from "../CollapsibleCard";
import { parseAsNumber, roundToDp } from "./utils";

interface LossFactorsCardProps {
  site: SiteAllowStringValues;
  esys:
    | AllowStringForNumbers<SiteElectricalSystem>
    | SiteElectricalSystemPlaceholder;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  pvInverterBrand: string;
  pvPanelName: string;
  isBulkEdit?: boolean;
  siteLossFactorsRaw: Record<string, any>;
}

const LossFactors = ({
  site,
  esys,
  setSite,
  pvInverterBrand,
  pvPanelName,
  isBulkEdit,
  siteLossFactorsRaw,
}: LossFactorsCardProps) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);
  const { fetchSiteValues } = useSite({
    app: "admin",
    enableNonEssentialQueries: true,
  });

  const siteComputedLossFactors = (
    (siteCalculations.aggHalfHourlyGeneration || {})?.genStack?.stack || []
  )
    .filter((factor) => factor.computed)
    .reduce((acc: Record<string, number>, curr) => {
      acc[curr.factor] = curr.pct;
      return acc;
    }, {});

  const defaultLossFactors = getDefaultsPvSystem(
    fetchSiteValues.data!.options.pvPanels,
    fetchSiteValues.data!.options.pvSystems,
    siteCalculations.currencyCode,
    pvInverterBrand,
    pvPanelName
  ).generationLossFactors;

  const nonDefaultLossFactorCount = fetchSiteValues
    .data!.options.pvLossFactorLabels.filter(
      (lossFactor: any, _index: number) => !lossFactor.deprecated
    )
    .map((lossFactor: any, _index: number) => {
      return (
        parseAsNumber(site.generationLossFactors?.[lossFactor.key]) !==
        (defaultLossFactors[lossFactor.key] ?? 0)
          ? 1
          : 0
      ) as number;
    })
    .reduce((a, b) => a + b, 0);

  return (
    <CollapsibleCard
      title={"Loss factors"}
      className="mt-2"
      isBulkEdit={false}
      initialOpen={false}
    >
      <Row className="d-flex flex-wrap align-items-end">
        <Col sm={4}>
          <Form.Group className="mb-3" controlId={`pvCurve-${esys.genIdx}`}>
            <Form.Label>Generation curve</Form.Label>
            <Form.Select
              value={site.pvCurve}
              onChange={(e) =>
                setSite({
                  pvCurve: e.target.value,
                })
              }
            >
              {Object.values(fetchSiteValues.data!.options.pvCurves).map(
                (pvCurve, index) => (
                  <option key={index} value={pvCurve["value"]}>
                    {pvCurve["label"]}
                  </option>
                )
              )}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group className="mb-3" controlId={`ghiSource-${esys.genIdx}`}>
            <Form.Label>GHI Source</Form.Label>
            <Form.Select
              value={site.ghiSource}
              onChange={(e) =>
                setSite({
                  ghiSource: e.target.value,
                })
              }
            >
              {Object.values(fetchSiteValues.data!.options.ghiSources).map(
                (ghiSource, index) => (
                  <option key={index} value={ghiSource["value"]}>
                    {ghiSource["label"]}
                  </option>
                )
              )}
            </Form.Select>
          </Form.Group>
        </Col>
        {!isBulkEdit && (
          <Col sm={4}>
            <Form.Group className="mb-3" controlId={`ghi-${esys.genIdx}`}>
              <Form.Label>GHI (kWh/m²)</Form.Label>
              <Form.Control
                type="text"
                value={
                  siteCalculations.generation?.[esys.genIdx]
                    ?.halfHourlyGeneration?.locationGhi ?? ""
                }
                disabled
              />
            </Form.Group>
          </Col>
        )}
        <Col sm={4}>
          <Form.Group className="mb-3" controlId={`ghiOverride-${esys.genIdx}`}>
            <Form.Label>GHI override (kWh/m²)</Form.Label>
            <Form.Control
              type="text"
              value={site.overrideGHI ?? ""}
              onChange={(e) =>
                setSite({
                  overrideGHI: e.target.value,
                })
              }
              isInvalid={!!errors.overrideGHI}
            />
            <Form.Control.Feedback type="invalid">
              {errors.overrideGHI}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group
            className="mb-3"
            controlId={`pvYoyForecastVariability-${esys.genIdx}`}
          >
            <Form.Label>YoY variability</Form.Label>
            <FormInputPercentage
              value={site.pvYoyForecastVariability}
              onChange={(e) =>
                setSite({
                  pvYoyForecastVariability: e.target.value,
                })
              }
              disabled={false}
              errorKey="pvYoyForecastVariability"
            />
          </Form.Group>
        </Col>
        {fetchSiteValues
          .data!.options.pvLossFactorLabels.filter(
            (lossFactor: any, _index: number) => !lossFactor.deprecated
          )
          .map((lossFactor: any, index: number) => {
            const computedLossFactorValue =
              siteComputedLossFactors?.[lossFactor.key];
            const forceManualLossFactorValue =
              site.generationLossFactorsOverride?.[lossFactor.key];
            const lossFactorValue =
              forceManualLossFactorValue ||
              computedLossFactorValue === undefined
                ? site.generationLossFactors?.[lossFactor.key]
                : computedLossFactorValue;
            const isComputedLossFactorValue =
              !forceManualLossFactorValue &&
              computedLossFactorValue !== undefined;
            const isValidNonDefault =
              !errors[`generationLossFactors.${lossFactor.key}`] &&
              (!isBulkEdit ||
                siteLossFactorsRaw[lossFactor.key] !== undefined) &&
              parseAsNumber(lossFactorValue) !==
                (defaultLossFactors[lossFactor.key] ?? 0);
            return (
              <Col key={index} sm={4}>
                <Form.Group
                  className="mb-3"
                  controlId={`lossFactor-${lossFactor.key}-${esys.genIdx}`}
                >
                  <Form.Label
                    style={{
                      fontWeight: isValidNonDefault ? "bold" : "normal",
                    }}
                  >
                    {lossFactor.label}
                  </Form.Label>
                  <Row>
                    <Col sm={computedLossFactorValue !== undefined ? 6 : 12}>
                      <FormInputPercentage
                        value={lossFactorValue ?? (isBulkEdit ? "" : 0)}
                        onChange={(e) =>
                          setSite({
                            generationLossFactors: {
                              ...siteLossFactorsRaw,
                              [lossFactor.key]: e.target.value,
                            },
                          })
                        }
                        disabled={isComputedLossFactorValue}
                        errorKey={`generationLossFactors.${lossFactor.key}`}
                        testId={lossFactor.key}
                      />
                      {isValidNonDefault && (
                        <div
                          style={{
                            fontSize: "0.875em",
                            marginTop: "0.25rem",
                          }}
                        >
                          Default:{" "}
                          {roundToDp(
                            (defaultLossFactors[lossFactor.key] ?? 0) * 100,
                            8
                          )}
                        </div>
                      )}
                    </Col>
                    {computedLossFactorValue !== undefined && (
                      <Col sm={6} style={{ paddingLeft: 0 }}>
                        <Form.Select
                          value={
                            site.generationLossFactorsOverride?.[lossFactor.key]
                              ? "true"
                              : "false"
                          }
                          onChange={(e) =>
                            setSite({
                              generationLossFactorsOverride: {
                                [lossFactor.key]: e.target.value === "true",
                              },
                            })
                          }
                        >
                          <option value={"false"}>Computed</option>
                          <option value={"true"}>Manual</option>
                        </Form.Select>
                      </Col>
                    )}
                  </Row>
                </Form.Group>
              </Col>
            );
          })}
        <Col sm={4} className="d-flex align-items-end mb-3">
          <Button
            variant="secondary"
            onClick={() =>
              setSite({
                generationLossFactors: {
                  ...defaultLossFactors,
                },
              })
            }
            disabled={nonDefaultLossFactorCount === 0}
            className="w-100"
          >
            Default loss factors
          </Button>
        </Col>
      </Row>
    </CollapsibleCard>
  );
};

export default LossFactors;
